package fr.frux.core.designsystem.components

import androidx.compose.material3.SnackbarDuration
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.SnackbarResult
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.rememberUpdatedState
import androidx.compose.runtime.snapshotFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.launch

/**
 * Affiche un message ponctuel résolu en composition, puis rend la main à l'écran pour qu'il le
 * consomme dans son ViewModel.
 *
 * Sur le web, `stringResource` renvoie d'abord `""` et ne charge la chaîne qu'ensuite (#438).
 * Un effet indexé sur la seule erreur partait avec ce texte vide, puis l'erreur était consommée
 * avant l'arrivée du vrai libellé. Ici l'effet est aussi indexé sur le texte et ignore une chaîne
 * vide : il attend le libellé chargé. Même garde pour [actionLabel], qui peut venir de la même
 * source.
 *
 * [trigger] est la valeur d'état qui porte le message (erreur, drapeau). Il garde l'effet
 * relancé quand deux messages identiques se suivent. [message] vaut `null` quand il n'y a rien à
 * afficher.
 */
@Composable
fun SnackbarMessageEffect(
    trigger: Any?,
    message: String?,
    snackbarHostState: SnackbarHostState,
    actionLabel: String? = null,
    duration: SnackbarDuration = if (actionLabel == null) SnackbarDuration.Short else SnackbarDuration.Indefinite,
    onShown: (SnackbarResult) -> Unit,
) {
    val currentOnShown by rememberUpdatedState(onShown)
    val currentTrigger by rememberUpdatedState(trigger)
    val currentMessage by rememberUpdatedState(message)
    val currentActionLabel by rememberUpdatedState(actionLabel)
    LaunchedEffect(trigger, message, actionLabel) {
        if (message.isNullOrEmpty() || actionLabel?.isEmpty() == true) return@LaunchedEffect
        var shown = false
        var displayed = false
        // Le message peut attendre derrière un autre snackbar du même hôte : tant qu'il n'est pas
        // réellement à l'écran, une destruction de l'effet (recréation d'entrée Nav3) ne l'acquitte pas.
        launch {
            snapshotFlow { snackbarHostState.currentSnackbarData }.first { it?.visuals?.message == message }
            displayed = true
        }
        try {
            val result = snackbarHostState.showSnackbar(message, actionLabel = actionLabel, duration = duration)
            shown = true
            currentOnShown(result)
        } finally {
            // showSnackbar suspend jusqu'à la fermeture : si l'écran quitte la composition pendant
            // l'affichage (navigation), le ViewModel n'était jamais acquitté et rejouait le message
            // au retour. Une annulation due à un nouveau message ne doit pas acquitter : cela
            // effacerait le nouveau dans le ViewModel. Les valeurs courantes distinguent les deux cas.
            val replaced =
                currentTrigger != trigger || currentMessage != message || currentActionLabel != actionLabel
            if (!shown && displayed && !replaced) currentOnShown(SnackbarResult.Dismissed)
        }
    }
}
